import { useLoad } from "@redotech/react-util/load";
import {
  ActivityStatus,
  Status,
} from "@redotech/redo-model/returns/temporal-status";
import { useContext, useState } from "react";
import { RpcClientContext } from "../../contexts/rpc";

export interface UseCreationStatusResult {
  creationStatus:
    | {
        activityStatuses: ActivityStatus[];
        workflowStartTime?: Date | undefined | null;
      }
    | undefined;
  completelyFinished: boolean;
  showDetailedStatus: boolean;
}

const SECONDS_UNTIL_SWITCHING_TO_DETAILED_STATUS = 25;
export const useCreationStatus = ({
  returnId,
}: {
  returnId: string;
}): UseCreationStatusResult => {
  const client = useContext(RpcClientContext);

  const [creationStatus, setCreationStatus] =
    useState<UseCreationStatusResult["creationStatus"]>(undefined);

  const [completelyFinished, setCompletelyFinished] = useState(true);

  useLoad(
    async (signal) => {
      if (!client || !returnId) {
        return;
      }

      try {
        const iterator = client.getReturnCreationStatus({ returnId });

        for await (const creationStatus of iterator) {
          setCreationStatus(creationStatus);
          setCompletelyFinished(
            creationStatus.activityStatuses.every(
              (status) =>
                status.status === Status.COMPLETED ||
                status.status === Status.FAILED,
            ),
          );
        }
      } catch (e) {
        if (signal.aborted) {
          return;
        }
        throw e;
      }
    },
    [client, returnId],
  );

  const secondsAfterWorkflowStartTime = creationStatus?.workflowStartTime
    ? new Date(
        creationStatus.workflowStartTime.getTime() +
          SECONDS_UNTIL_SWITCHING_TO_DETAILED_STATUS * 1000,
      )
    : undefined;
  // After 25 seconds, we should show the detailed status and what is taking so long, this could technically take hours or days if Shopify were to go down for example.
  const showDetailedStatus = secondsAfterWorkflowStartTime
    ? new Date() > secondsAfterWorkflowStartTime
    : false;

  return { creationStatus, completelyFinished, showDetailedStatus };
};
